import {
  collection,
  addDoc,
  setDoc,
  doc,
  arrayUnion,
  Timestamp,
  updateDoc,
  deleteDoc,
  arrayRemove,
  runTransaction,
} from "firebase/firestore";
import {
  AIChat,
  ConversationChunk,
  ConversationMessage,
  ConversationOverview,
  User,
} from "./types";
import { db } from "./firebase";

const touchConversationFirestore = async (
  userId: string,
  conversationId: string
) => {
  const userRef = doc(db, `users/${userId}`);

  await runTransaction(db, async (transaction) => {
    const userSnapshot = await transaction.get(userRef);
    if (!userSnapshot.exists()) return;

    const conversations = [
      ...((userSnapshot.data().conversations || []) as ConversationOverview[]),
    ];
    const conversationIndex = conversations.findIndex(
      (conversation) => conversation.id === conversationId
    );
    if (conversationIndex === -1) return;

    const conversation = conversations.splice(conversationIndex, 1)[0];
    conversations.push({ ...conversation, updatedAt: Timestamp.now() });

    transaction.set(userRef, { conversations }, { merge: true });
  });
};

export const createChunkFirestore = async (
  userData: User,
  conversationId: string,
  chunk: ConversationChunk
) => {
  const userId = userData.uid;
  const chunksRef = collection(
    db,
    `users/${userId}/conversations/${conversationId}/chunks`
  );

  // Create the chunk document
  await setDoc(doc(chunksRef, chunk.id), chunk);
  await touchConversationFirestore(userId, conversationId);
};

export const addMessageToChunkFirestore = async (
  userData: User,
  conversationId: string,
  chunkId: string,
  message: ConversationMessage
) => {
  const userId = userData.uid;
  const chunkRef = doc(
    db,
    `users/${userId}/conversations/${conversationId}/chunks/${chunkId}`
  );

  // Update the chunk with the new message
  await setDoc(
    chunkRef,
    {
      conversationMessages: arrayUnion(message),
    },
    { merge: true }
  );
  await touchConversationFirestore(userId, conversationId);
};

export const updateChunkAIChatFirestore = async (
  userData: User,
  conversationId: string,
  chunkId: string,
  aiChat: AIChat | null
) => {
  const userId = userData.uid;
  const chunkRef = doc(
    db,
    `users/${userId}/conversations/${conversationId}/chunks/${chunkId}`
  );

  // Update the chunk with AI chat
  await setDoc(
    chunkRef,
    {
      aiChat: aiChat,
    },
    { merge: true }
  );
  await touchConversationFirestore(userId, conversationId);
};

export const updateChunkAIChatSummaryFirestore = async (
  userData: User,
  conversationId: string,
  chunkId: string,
  aiChatSummary: string | string[]
) => {
  const userId = userData.uid;
  const chunkRef = doc(
    db,
    `users/${userId}/conversations/${conversationId}/chunks/${chunkId}`
  );

  // Ensure we're saving as an array
  const summaryArray = Array.isArray(aiChatSummary)
    ? aiChatSummary
    : [aiChatSummary];

  // Update the chunk with AI chat summary
  await setDoc(
    chunkRef,
    {
      aiChatSummary: summaryArray,
    },
    { merge: true }
  );
  await touchConversationFirestore(userId, conversationId);
};

export const createNewConversation = async (
  userId: string,
  targetName: string
) => {
  // Create a reference with auto-generated ID
  const conversationRef = doc(collection(db, `users/${userId}/conversations`));
  const conversationId = conversationRef.id;
  const timestamp = Timestamp.now();

  // Create the conversation document
  await setDoc(conversationRef, {
    createdAt: timestamp,
  });

  // Create initial chunk document
  const chunksRef = doc(
    collection(db, `users/${userId}/conversations/${conversationId}/chunks`)
  );
  await setDoc(chunksRef, {
    id: chunksRef.id,
    conversationMessages: [],
    timestamp: timestamp,
  });

  // Add conversation overview to user document
  const userRef = doc(db, `users/${userId}`);
  await setDoc(
    userRef,
    {
      conversations: arrayUnion({
        id: conversationId,
        targetName: targetName.trim(),
        updatedAt: timestamp,
      }),
    },
    { merge: true }
  );

  return conversationId;
};

export const updateChunkMessagesFirestore = async (
  userData: User,
  conversationId: string,
  chunkId: string,
  messages: ConversationMessage[]
) => {
  if (!userData?.uid) throw new Error("No user ID provided");

  const chunkRef = doc(
    db,
    `users/${userData.uid}/conversations/${conversationId}/chunks`,
    chunkId
  );

  await updateDoc(chunkRef, {
    conversationMessages: messages,
  });
  await touchConversationFirestore(userData.uid, conversationId);
};

export const deleteConversationFirestore = async (
  userData: User,
  conversationId: string
) => {
  const userId = userData.uid;

  const userRef = doc(db, `users/${userId}`);

  const conversationToRemove = userData?.conversations?.find(
    (conv: ConversationOverview) => conv.id === conversationId
  );

  if (conversationToRemove) {
    console.log("conversationToRemove", conversationToRemove);
    // Remove the conversation from the user's conversations array
    await updateDoc(userRef, {
      conversations: arrayRemove(conversationToRemove),
    });
  }

  // Delete the conversation document
  // Don't delete the conversation yet, we want to keep the conversation history
  // const conversationRef = doc(
  //   db,
  //   `users/${userId}/conversations/${conversationId}`
  // );
  // await deleteDoc(conversationRef);
};

export const updateConversationNameFirestore = async (
  userId: string,
  conversationId: string,
  newName: string
) => {
  const userRef = doc(db, `users/${userId}`);

  await runTransaction(db, async (transaction) => {
    const userSnapshot = await transaction.get(userRef);
    if (!userSnapshot.exists()) return;

    const conversations = (
      (userSnapshot.data().conversations || []) as ConversationOverview[]
    ).map((conversation) =>
      conversation.id === conversationId
        ? { ...conversation, targetName: newName.trim() }
        : conversation
    );

    transaction.set(userRef, { conversations }, { merge: true });
  });
};

export const updateConversationSummaryFirestore = async (
  userData: User,
  conversationId: string,
  summary: string[]
) => {
  if (!userData?.uid) throw new Error("No user ID provided");

  const conversationRef = doc(
    db,
    `users/${userData.uid}/conversations/${conversationId}`
  );

  // Update the conversation document with the summary
  await setDoc(
    conversationRef,
    {
      summary: summary,
    },
    { merge: true }
  );
};
